// Autosave for the Fee Slips & Invoices page.
//
// A status or note changed on a row used to be kept only in the page until "Save status &
// notes" was pressed, so a morning's work was lost to a reload, a closed tab or simply
// moving on (16 Oct 2026). Now every change is:
//   1. saved to the record on its own, a moment after typing stops; and
//   2. kept in the browser tab until it has saved, so a reload brings it back. The copy
//      goes through clientStorage, which on the hosted portal is the tab's session
//      storage: client details are not left on the computer once the tab is closed, and
//      closing a tab with a change still unsaved asks first.
//
// One kind of change is not saved on its own, and the row says so: Paid / Partial paid on
// the admin page, which also records a payment (date, amount, type) - that is a deliberate
// step, and saving it half-filled would record the wrong amount.

import { clientStorage } from "./clientStorage.js";
import { normalizePaymentStatus } from "./clerkStatements.js";

export const AUTOSAVE_DELAY_MS = 1500;

// Changes kept in the browser are dropped after a week: older ones are more likely to undo
// something saved since than to be work worth keeping.
export const STORED_EDITS_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export const storedEditsKey = (brand) => `cls_fee_slip_edits_v1:${String(brand || "").toLowerCase()}`;

const PAYING_STATUSES = new Set(["paid", "partial_paid"]);

const defaultStorage = () => clientStorage;

export function readStoredEdits(key, { storage = defaultStorage(), now = Date.now() } = {}) {
  try {
    const parsed = JSON.parse(storage?.getItem(key) || "null");
    if (!parsed || typeof parsed.edits !== "object" || Array.isArray(parsed.edits)) return {};
    if (!(now - Number(parsed.savedAt) <= STORED_EDITS_MAX_AGE_MS)) return {};
    return parsed.edits;
  } catch {
    return {};
  }
}

export function writeStoredEdits(key, edits, { storage = defaultStorage(), now = Date.now() } = {}) {
  try {
    if (!storage) return;
    if (!edits || !Object.keys(edits).length) storage.removeItem(key);
    else storage.setItem(key, JSON.stringify({ savedAt: now, edits }));
  } catch {
    // Storage full or blocked: the page still works, it just cannot keep a copy.
  }
}

// The fields of the "Record payment" box. A row already Paid or Partial paid shows that box
// without its status changing, so a change to any of these is a payment too.
const PAYMENT_FIELDS = ["paidOn", "amountPaid", "paymentType", "sentBy"];

// Why a row's change waits for "Save", or "" when it saves on its own.
export function autosaveBlocker(item = {}, edit = {}, { isAdminMode = false } = {}) {
  const status = edit.status ? normalizePaymentStatus(edit.status) : "";
  if (isAdminMode && status && PAYING_STATUSES.has(status)) return "payment";
  // Changing the date or amount on a row that was already Paid used to save on its own and
  // record the payment, and pressing Save as well recorded it a second time (16 Oct 2026).
  const currentStatus = item.status ? normalizePaymentStatus(item.status) : "";
  if (
    isAdminMode && !status && PAYING_STATUSES.has(currentStatus)
    && PAYMENT_FIELDS.some(field => edit[field] !== undefined)
  ) return "payment";
  return "";
}

export const BLOCKER_MESSAGES = {
  payment: "Not saved yet: press Save or Save all to record the payment.",
};

export const sameEdit = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// "Save all" (Emma, 3 Nov 2026): the rows of a list that have a change waiting.
export function rowsToSave(rows = [], edits = {}) {
  return rows.filter((row) => edits[row.id]).map((item) => ({ item, edit: edits[item.id] }));
}

// A payment cannot be recorded without a type, and an emailed item nobody has sorted has none
// until it is chosen. Refusing those one by one left a whole list unsaved (Emma, 3 Nov 2026),
// so "Save all" asks once and applies the answer to the lines still without one.
export const needsPaymentType = (plan) => plan.recording?.amount_paid > 0 && !plan.recording.payment_type;

export function withPaymentType(plans = [], paymentType = "") {
  return plans.map((plan) => (needsPaymentType(plan)
    ? {
      ...plan,
      edit: { ...plan.edit, paymentType },
      recording: { ...plan.recording, payment_type: paymentType },
    }
    : plan));
}

const lines = (count) => `${count} line${count === 1 ? "" : "s"}`;
const paymentsWord = (count) => `${count} payment${count === 1 ? "" : "s"}`;

// What to ask before "Save all" records money, or "" when it records none. plans are
// rowsToSave's rows with the payment each would record (null when it records none).
export function saveAllQuestion(plans = [], formatMoney = String) {
  const paying = plans.filter((plan) => plan.recording?.amount_paid > 0);
  const noAmount = plans.filter((plan) => plan.recording && !(plan.recording.amount_paid > 0));
  if (!paying.length && !noAmount.length) return "";
  const total = paying.reduce((sum, plan) => sum + plan.recording.amount_paid, 0);
  const parts = [`Save ${lines(plans.length)}?`];
  if (paying.length) {
    parts.push(`This records ${paymentsWord(paying.length)} totalling ${formatMoney(total)}, each with the date and amount shown on its line.`);
  }
  if (noAmount.length) {
    const names = noAmount.map((plan) => plan.item.clientName).join(", ");
    parts.push(`No payment is recorded for ${lines(noAmount.length)} with no amount: ${names}.`);
  }
  return parts.join("\n\n");
}

// The message after "Save all". results are what each row's save returned.
export function saveAllOutcome(results = [], formatMoney = String) {
  const saved = results.filter((result) => result?.saved);
  const paid = saved.filter((result) => result.paid > 0);
  const duplicates = saved.filter((result) => result.duplicate);
  const parts = [];
  if (saved.length) parts.push(`Saved ${lines(saved.length)}.`);
  if (paid.length) {
    const total = paid.reduce((sum, result) => sum + result.paid, 0);
    parts.push(`${paymentsWord(paid.length)} recorded, ${formatMoney(total)}.`);
  }
  if (duplicates.length) parts.push(`${paymentsWord(duplicates.length)} already recorded, so not added again.`);
  return { message: parts.join(" "), notSaved: results.length - saved.length };
}

// The next row to save on its own: one with a change that is not blocked and has not
// already failed as it stands (a failed change is tried again once it is changed, or by
// pressing Save).
export function nextAutosave(edits = {}, itemsById = new Map(), { isAdminMode = false, failed = new Map() } = {}) {
  for (const [id, edit] of Object.entries(edits)) {
    const item = itemsById.get(id);
    if (!item) continue;
    if (autosaveBlocker(item, edit, { isAdminMode })) continue;
    if (failed.has(id) && sameEdit(failed.get(id), edit)) continue;
    return { id, item, edit };
  }
  return null;
}
